import React from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { Modal } from '../../components';
import get from 'lodash/get';
import moment from 'moment';
import { txLastTransition } from '../../util/transaction';
import {
  TRANSITION_TRANSFERRED,
  TRANSITION_REQUEST_EXTEND_BOOKING,
  TRANSITION_OFFER_EXTEND_BOOKING_DIRECTLY,
  TRANSITION_OFFER_EXTEND_BOOKING,
} from '../../util/customTransaction/mainRentalTransaction';
import {
  TRANSITION_EXTEND_PAYOUT_ADDITION_FEE,
  TRANSITION_EXTEND_REQUEST_PAYMENT_FREE,
  TRANSITION_EXTEND_REQUEST_EXTEND_BOOKING,
  TRANSITION_EXTEND_OFFER_EXTEND_BOOKING_DIRECTLY,
  TRANSITION_EXTEND_OFFER_EXTEND_BOOKING,
} from '../../util/customTransaction/extendRentalTransaction';
import { ExtendRentalForm } from '../../forms';

import css from './ExtendRentalModal.css';
import { shallowEqual, useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';


const getNextTransitionForExtendBooking = (isProvider, isCustomer, lastTransition) => {
  switch (lastTransition) {
    case TRANSITION_TRANSFERRED:
      if (isProvider)
        return TRANSITION_OFFER_EXTEND_BOOKING_DIRECTLY;
      else if (isCustomer)
        return TRANSITION_REQUEST_EXTEND_BOOKING;
      else return null;

    case TRANSITION_EXTEND_PAYOUT_ADDITION_FEE:
    case TRANSITION_EXTEND_REQUEST_PAYMENT_FREE:
      if (isProvider)
        return TRANSITION_EXTEND_OFFER_EXTEND_BOOKING_DIRECTLY;
      else if (isCustomer)
        return TRANSITION_EXTEND_REQUEST_EXTEND_BOOKING;
      else return null;

    case TRANSITION_REQUEST_EXTEND_BOOKING:
      if (isProvider)
        return TRANSITION_OFFER_EXTEND_BOOKING;
      else return null;

    case TRANSITION_EXTEND_REQUEST_EXTEND_BOOKING:
      if (isProvider)
        return TRANSITION_EXTEND_OFFER_EXTEND_BOOKING;
      else return null;

    default:
      return null;
  }
}

const ExtendRentalModal = props => {
  const {
    className,
    rootClassName,
    id,
    intl,
    isOpen,
    isCustomer,
    isProvider,
    onCloseModal,
    onManageDisableScrolling,
    transaction,
    onSubmitExtendRental,
    extendInProgress,
    extendError,
    customerDisplayName,
    listingTitle,
    timeSlots,
    listing,
    isOwnListing,
    onFetchExtendTransactionLineItem,
    fetchLineItemsInProgress,
    lineItems,
    fetchLineItemsError,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const closeButtonMessage = intl.formatMessage({ id: 'ExtendRentalModal.close' });
  const lastTransition = txLastTransition(transaction);

  const createHoldingSlotTxInProgress = useSelector((state) =>
    state.TransactionPage.createHoldingSlotTxInProgress, shallowEqual);
  const createHoldingSlotTxError = useSelector((state) =>
    state.TransactionPage.createHoldingSlotTxError, shallowEqual);

  const isCustomerExtendedFirst =
    lastTransition === TRANSITION_REQUEST_EXTEND_BOOKING ||
    lastTransition === TRANSITION_EXTEND_REQUEST_EXTEND_BOOKING;

  const bookingEnd = get(transaction, 'booking.attributes.end', null);
  const currentRentalSeats = get(transaction, 'booking.attributes.seats', null);
  const { startDate, endDate } = get(transaction, 'attributes.protectedData.extendBookingData', {});

  const firstDateAvailable = get(timeSlots, '[0].attributes.start', bookingEnd);

  const extendStartDate = bookingEnd;
    // moment(bookingEnd).diff(moment(firstDateAvailable), 'days') >= 0
    // ? bookingEnd
    // : firstDateAvailable;

  const initialValues = isCustomerExtendedFirst && startDate && endDate
    ? {
      bookingDates: {
        startDate: new Date(startDate),
        endDate: new Date(endDate),
      }
    } : {
      bookingDates: {
        startDate: extendStartDate,
      }
    };

  return (
    <Modal
      id={id}
      containerClassName={classes}
      contentClassName={css.modalContent}
      isOpen={isOpen}
      onClose={onCloseModal}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      closeButtonMessage={closeButtonMessage}
    >
      <p className={css.modalTitle}>
        <FormattedMessage
          id="ExtendRentalModal.title"
          values={{ renterName: customerDisplayName }}
        />
      </p>
      <p className={css.modalMessage}>
        <FormattedMessage
          id="ExtendRentalModal.itemName"
          values={{ listingTitle: <span className={css.itemName}>{listingTitle}</span> }}
        />
      </p>
      <ExtendRentalForm
        initialValues={initialValues}
        timeSlots={timeSlots}
        onSubmit={values => {
          const nextTransition = getNextTransitionForExtendBooking(isProvider, isCustomer, lastTransition);

          const updatedValues = {
            transactionId: transaction.id,
            transition: nextTransition,
            seats: currentRentalSeats,
            ...values,
          };
          onSubmitExtendRental(updatedValues);
        }}
        listingId={listing.id}
        transactionId={transaction.id}
        formId="ExtendRental"
        isOwnListing={isOwnListing}
        isCustomer={isCustomer}
        isProvider={isProvider}
        onFetchExtendTransactionLineItem={onFetchExtendTransactionLineItem}
        fetchLineItemsInProgress={fetchLineItemsInProgress}
        fetchLineItemsError={fetchLineItemsError}
        lineItems={lineItems}
        extendInProgress={extendInProgress}
        extendError={extendError}
        bookingEnd={bookingEnd}
        currentRentalSeats={currentRentalSeats}
        createHoldingSlotTxInProgress={createHoldingSlotTxInProgress}
        createHoldingSlotTxError={createHoldingSlotTxError}
        listing={listing}
      />
    </Modal>
  );
};

const { string, bool } = PropTypes;

ExtendRentalModal.defaultProps = {
  className: null,
  rootClassName: null,
};

ExtendRentalModal.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  isCustomer: bool,
  isProvider: bool,
};

export default injectIntl(ExtendRentalModal);
